import type {
  V1Affix,
  V1Content,
  V1Enemy,
  V1Equipment,
  V1Item,
  V1LifeSteal,
  V1Map,
  V1MapBoss,
  V1MapCell,
  V1QualityProfile,
  V1Skill,
  V1Stats,
  V1Trait,
  V1Troop,
} from "./types";
import { v1EquipmentSlotsForType } from "./equipment-slots";
import {
  rollV1SkillOutcome,
  v1ApplyParameterRates,
  v1RuntimeBattler,
  v1SkillBaseDamage,
} from "./battle-rules";

const SAVE_KEY = "jintu-rpg-game2-save";
const COMPATIBLE_SAVE_CONTENT_HASHES = new Set([
  "a4a493da5dfe37759ad879175188ceb240efe0790310a89abdb3115a8c5e4c15",
  "fd58806191bb38ea80af35fdc83cd27f0550ed191aa2e759bf00f4e60e24e101",
  "4fa28f9a4359c70d9fbf6c4db8400e8670dfc2d8461386b93055e6279c8671fa",
  "152a1d012b96f1fc105ee5e48b021021496a5dbb6cb6957dbe85471f7fd19af8",
  "e19868a2839a55e252c33b6f456fe31698a16f468266c2c30e681816e2807ec1",
  "524a709d18103a7374218eebe09c527749875bad73b9af7fbc3f3e258fdd8d99",
  "26f1015ff7c87972f538255bef2f72639790c8b5c06e53fa5d3275de0684737f",
  "b55a796a670dbb3e63fb5ae9c4063ef8807d66230b4635ca9bcd43e8c4846290",
  "3a8356d5fedb140d7b2a0d3e8416fb17398f75ab224ab556f035239a4f849465",
  "a199f79d446e85fbe178d9656cd4e186095881f60409d81b63870d634b47d12c",
]);
const WORLD_SEED = 0x4a494e54;
const TOAST_MS = 2_500;
const PLAYER_COMBAT_FX_MS = 950;
const VICTORY_RESOLVE_DELAY_MS = PLAYER_COMBAT_FX_MS + 100;
const NORMAL_MONSTER_MOVE_INTERVAL_SECONDS = 1.25;
const NORMAL_MONSTER_MOVE_CHANCE = 0.8;

const STAT_KEYS = [
  "maxHp", "maxMp", "attack", "defense",
  "magicAttack", "magicDefense", "agility", "luck",
] as const;

export interface V1AffixRoll {
  lineNo: number;
  affixId: number;
  randomValue: number | null;
  statAllocations?: Array<{ statCode: number; value: number }>;
}

export interface V1EquipmentInstance {
  instanceId: string;
  equipmentId: number;
  affixRolls: V1AffixRoll[];
  locked: boolean;
  bossEnemyId?: number;
}

export interface V1DerivedEquipment extends V1EquipmentInstance {
  name: string;
  qualityId: number;
  finalPrice: number;
  stats: V1Stats;
}

export interface V1EquipmentComparison {
  targetSlotId: number;
  current: V1DerivedEquipment | null;
  deltas: V1Stats;
}

export interface V1NormalMonster {
  instanceId: string;
  troopId: number;
  x: number;
  y: number;
}

export interface V1BossRuntime {
  defeatedAt: number;
  nextRespawnAt: number | null;
}

export interface V1MapRuntimeState {
  rngState: number;
  normalSequence: number;
  normalMonsters: V1NormalMonster[];
  normalRespawnDueAt: number[];
  normalMonsterNextMoveAt: number;
  bosses: Record<string, V1BossRuntime>;
}

export interface V1BattleEnemy {
  index: number;
  enemyId: number;
  name: string;
  hp: number;
  maxHp: number;
  spriteSheet: number;
  spriteIndex: number;
  rank: number;
}

export interface V1BattleState {
  troopId: number;
  encounterKey: string;
  kind: "normal" | "boss";
  enemies: V1BattleEnemy[];
  selectedIndex: number;
  round: number;
  auto: boolean;
  canEscape: boolean;
  log: string[];
  status: "active" | "victory" | "defeat";
  autoElapsedMs: number;
  pendingEnemyTurn: boolean;
  pendingEnemyMs: number;
  victoryResolveMs: number;
  defeatReturnMs: number;
}

export interface V1CombatFx {
  sequence: number;
  animationId: number;
  targetIndex: number;
  amount: number;
  durationMs: number;
  kind: "damage" | "healing";
}

export interface V1LootReview {
  exp: number;
  gold: number;
  items: Array<{ itemId: number; quantity: number }>;
  equipment: V1EquipmentInstance[];
  sourceKind: "normal" | "boss";
  firstBossDefeated: boolean;
}

export interface V1PlayerState {
  level: number;
  exp: number;
  gold: number;
  hp: number;
  mp: number;
  currentMapId: number;
  x: number;
  y: number;
  facingX: number;
  facingY: number;
  items: Record<number, number>;
  inventory: V1EquipmentInstance[];
  equipment: Record<number, V1EquipmentInstance>;
  learnedSkills: number[];
}

export type V1Panel = "inventory" | "skills" | "map" | "shop" | null;

export interface V1GameSnapshot {
  version: 1;
  contentHash: string;
  started: boolean;
  player: V1PlayerState;
  mapRuntime: Partial<Record<number, V1MapRuntimeState>>;
  gameSeconds: number;
  equipmentSequence: number;
  combatSequence: number;
  combatFx: V1CombatFx | null;
  autoBattlePreference: boolean;
  autoSellMaxQualityId: number;
  battle: V1BattleState | null;
  loot: V1LootReview | null;
  openPanel: V1Panel;
  activeShopId: number | null;
  dialogue: { speaker: string; lines: string[] } | null;
  toast: string | null;
  firstBossDefeated: boolean;
  gameOver: boolean;
}

type Listener = (snapshot: V1GameSnapshot) => void;

const clamp = (value: number, minimum: number, maximum: number) =>
  Math.min(maximum, Math.max(minimum, value));

const hash32 = (seed: number, mapId: number): number => {
  let value = (seed ^ Math.imul(mapId, 0x9e3779b1)) >>> 0;
  value ^= value >>> 16;
  value = Math.imul(value, 0x85ebca6b) >>> 0;
  value ^= value >>> 13;
  value = Math.imul(value, 0xc2b2ae35) >>> 0;
  value ^= value >>> 16;
  return value || 0x6d2b79f5;
};

const xorshift32 = (state: number): number => {
  let value = state >>> 0;
  value ^= value << 13;
  value ^= value >>> 17;
  value ^= value << 5;
  return value >>> 0 || 0x6d2b79f5;
};

const emptyStats = (): V1Stats => ({
  maxHp: 0,
  maxMp: 0,
  attack: 0,
  defense: 0,
  magicAttack: 0,
  magicDefense: 0,
  agility: 0,
  luck: 0,
});

const addStats = (target: V1Stats, source: V1Stats): void => {
  for (const key of STAT_KEYS) target[key] += source[key];
};

const statByCode = (stats: V1Stats, code: number): keyof V1Stats | null =>
  STAT_KEYS[code] ?? null;

const lifeStealTotals = (
  mechanics: readonly V1LifeSteal[],
  channel: number,
): { rate: number; flat: number } => {
  let remainingRate = 1;
  let flat = 0;
  for (const entry of mechanics) {
    if (entry.channel !== channel) continue;
    if (entry.mode === 1) remainingRate *= 1 - entry.value / 100;
    else if (entry.mode === 2) flat += entry.value;
  }
  return { rate: 1 - remainingRate, flat };
};

const lifeStealForDamage = (
  damage: number,
  totals: { rate: number; flat: number },
): number => Math.min(
  damage,
  Math.max(0, Math.floor(damage * totals.rate + totals.flat)),
);

const cellKey = (mapId: number, x: number, y: number, troopId: number) =>
  `b:${mapId}:${x}:${y}:${troopId}`;

export class V1GameEngine {
  readonly content: V1Content;
  private state: V1GameSnapshot;
  private readonly listeners = new Set<Listener>();
  private cloudSaveHandler: ((saveJson: string) => void | Promise<void>) | null = null;
  private toastRemainingMs = 0;

  constructor(content: V1Content) {
    this.content = content;
    this.state = this.initialSnapshot();
  }

  get snapshot(): V1GameSnapshot {
    return this.state;
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    const stats = this.playerStats();
    this.state.player.hp = clamp(this.state.player.hp, 0, stats.maxHp);
    this.state.player.mp = clamp(this.state.player.mp, 0, stats.maxMp);
    for (const listener of this.listeners) listener(this.state);
  }

  private notify(message: string): void {
    this.state.toast = message;
    this.toastRemainingMs = TOAST_MS;
    this.emit();
  }

  private initialSnapshot(): V1GameSnapshot {
    const level = this.content.indexes.levels.get(this.content.actor.initialLevel);
    if (!level) throw new Error("开局等级数据缺失");
    const entry = this.entryPosition(this.content.rules.startMapId, this.content.rules.startEntryId);
    const items = Object.fromEntries(
      this.content.rules.initialItems.map(({ itemId, quantity }) => [itemId, quantity]),
    );
    const learnedSkills = this.content.levels
      .filter((row) => row.level <= this.content.actor.initialLevel)
      .flatMap((row) => row.learnSkills.map((entry) => entry.skillId));
    return {
      version: 1,
      contentHash: this.content.manifest.contentHash,
      started: false,
      player: {
        level: this.content.actor.initialLevel,
        exp: 0,
        gold: this.content.rules.initialGold,
        hp: level.maxHp,
        mp: level.maxMp,
        currentMapId: this.content.rules.startMapId,
        x: entry.x,
        y: entry.y,
        facingX: 1,
        facingY: 0,
        items,
        inventory: [],
        equipment: {},
        learnedSkills: [...new Set(learnedSkills)],
      },
      mapRuntime: {},
      gameSeconds: 0,
      equipmentSequence: 0,
      combatSequence: 0,
      combatFx: null,
      autoBattlePreference: this.content.rules.autoBattleDefault,
      autoSellMaxQualityId: 0,
      battle: null,
      loot: null,
      openPanel: null,
      activeShopId: null,
      dialogue: null,
      toast: null,
      firstBossDefeated: false,
      gameOver: false,
    };
  }

  newGame(): void {
    this.state = this.initialSnapshot();
    this.state.started = true;
    this.ensureMapRuntime(this.currentMap());
    this.notify("先在旧村击败普通怪，获取并穿戴装备。");
  }

  enterTitle(): void {
    this.state.started = false;
    this.state.battle = null;
    this.state.loot = null;
    this.state.openPanel = null;
    this.state.dialogue = null;
    this.emit();
  }

  hasSave(): boolean {
    return typeof window !== "undefined" && window.localStorage.getItem(SAVE_KEY) !== null;
  }

  localSaveJson(): string | null {
    if (typeof window === "undefined") return null;
    return window.localStorage.getItem(SAVE_KEY);
  }

  setCloudSaveHandler(
    handler: ((saveJson: string) => void | Promise<void>) | null,
  ): void {
    this.cloudSaveHandler = handler;
  }

  save(): boolean {
    if (typeof window === "undefined" || !this.state.started || this.state.battle || this.state.loot) return false;
    try {
      const saveJson = JSON.stringify(this.state);
      window.localStorage.setItem(SAVE_KEY, saveJson);
      if (this.cloudSaveHandler) {
        try {
          void Promise.resolve(this.cloudSaveHandler(saveJson)).catch(() => undefined);
        } catch {
          // 本地保存成功不应被云端同步失败回滚。
        }
      }
      this.notify("进度已保存。");
      return true;
    } catch {
      return false;
    }
  }

  resume(): boolean {
    if (typeof window === "undefined") return false;
    try {
      const raw = window.localStorage.getItem(SAVE_KEY);
      if (!raw) return false;
      return this.restoreSaveJson(raw, "存档已恢复。");
    } catch {
      return false;
    }
  }

  importCloudSaveJson(raw: string): boolean {
    if (typeof window === "undefined") return false;
    const restored = this.restoreSaveJson(raw, "云存档已读取，并同步到本机。");
    if (!restored) return false;
    try {
      window.localStorage.setItem(SAVE_KEY, JSON.stringify(this.state));
      return true;
    } catch {
      return false;
    }
  }

  private restoreSaveJson(raw: string, message: string): boolean {
    try {
      const parsed = JSON.parse(raw) as V1GameSnapshot;
      const sourceContentHash = parsed.contentHash;
      if (
        parsed.version !== 1 ||
        (
          parsed.contentHash !== this.content.manifest.contentHash
          && !COMPATIBLE_SAVE_CONTENT_HASHES.has(parsed.contentHash)
        ) ||
        !this.content.indexes.maps.has(parsed.player.currentMapId)
      ) {
        return false;
      }
      parsed.contentHash = this.content.manifest.contentHash;
      parsed.battle = null;
      parsed.loot = null;
      parsed.dialogue = null;
      parsed.openPanel = null;
      parsed.activeShopId = null;
      parsed.toast = null;
      parsed.combatSequence ??= 0;
      parsed.combatFx = null;
      parsed.autoBattlePreference ??= this.content.rules.autoBattleDefault;
      parsed.autoSellMaxQualityId = clamp(
        Math.floor(Number(parsed.autoSellMaxQualityId ?? 0)),
        0,
        5,
      );
      parsed.started = true;
      this.state = parsed;
      this.revalidateEquipmentInstances(sourceContentHash);
      this.ensureMapRuntime(this.currentMap());
      this.notify(message);
      return true;
    } catch {
      return false;
    }
  }

  loadSave(): boolean {
    const loaded = this.resume();
    if (!loaded) this.notify("没有可读取的有效存档。");
    return loaded;
  }

  tick(deltaMs: number): void {
    if (!this.state.started) return;
    const bounded = clamp(deltaMs, 0, 250);
    this.state.gameSeconds += bounded / 1_000;
    const battle = this.state.battle;
    if (battle?.status === "victory") {
      battle.victoryResolveMs = Math.max(0, battle.victoryResolveMs - bounded);
      if (battle.victoryResolveMs === 0) this.resolveVictory(battle);
    } else if (battle?.pendingEnemyTurn) {
      battle.pendingEnemyMs = Math.max(0, battle.pendingEnemyMs - bounded);
      if (battle.pendingEnemyMs === 0) this.resolveEnemyTurn(battle);
    } else if (battle?.status === "defeat") {
      battle.defeatReturnMs = Math.max(0, battle.defeatReturnMs - bounded);
      if (battle.defeatReturnMs === 0) this.resolveDefeat();
    }
    if (this.state.toast) {
      this.toastRemainingMs -= bounded;
      if (this.toastRemainingMs <= 0) this.state.toast = null;
    }
    this.refreshCurrentMap();
    this.emit();
  }

  currentMap(): V1Map {
    const map = this.content.indexes.maps.get(this.state.player.currentMapId);
    if (!map) throw new Error(`当前地图不存在：${this.state.player.currentMapId}`);
    return map;
  }

  currentMapRuntime(): V1MapRuntimeState {
    return this.ensureMapRuntime(this.currentMap());
  }

  private ensureMapRuntime(map: V1Map): V1MapRuntimeState {
    let runtime = this.state.mapRuntime[map.id];
    if (!runtime) {
      runtime = {
        rngState: hash32(WORLD_SEED, map.id),
        normalSequence: 0,
        normalMonsters: [],
        normalRespawnDueAt: [],
        normalMonsterNextMoveAt: this.state.gameSeconds + NORMAL_MONSTER_MOVE_INTERVAL_SECONDS,
        bosses: {},
      };
      this.state.mapRuntime[map.id] = runtime;
      while (runtime.normalMonsters.length < map.normalMaxAlive) {
        if (!this.spawnNormalMonster(map, runtime)) break;
      }
    }
    if (!Number.isFinite(runtime.normalMonsterNextMoveAt)) {
      runtime.normalMonsterNextMoveAt = this.state.gameSeconds + NORMAL_MONSTER_MOVE_INTERVAL_SECONDS;
    }
    return runtime;
  }

  private nextRandom(runtime: V1MapRuntimeState): number {
    runtime.rngState = xorshift32(runtime.rngState);
    return runtime.rngState / 0x1_0000_0000;
  }

  private refreshCurrentMap(): void {
    const map = this.currentMap();
    const runtime = this.ensureMapRuntime(map);
    while (runtime.normalRespawnDueAt[0] !== undefined && runtime.normalRespawnDueAt[0] <= this.state.gameSeconds) {
      if (runtime.normalMonsters.length >= map.normalMaxAlive) {
        runtime.normalRespawnDueAt.shift();
        continue;
      }
      if (!this.spawnNormalMonster(map, runtime)) break;
      runtime.normalRespawnDueAt.shift();
    }
    for (const [key, boss] of Object.entries(runtime.bosses)) {
      if (boss.nextRespawnAt !== null && boss.nextRespawnAt <= this.state.gameSeconds) delete runtime.bosses[key];
    }
    const canMoveMonsters = !this.state.battle
      && !this.state.loot
      && !this.state.openPanel
      && !this.state.dialogue
      && !this.state.gameOver;
    if (canMoveMonsters && runtime.normalMonsterNextMoveAt <= this.state.gameSeconds) {
      this.moveNormalMonsters(map, runtime);
      runtime.normalMonsterNextMoveAt = this.state.gameSeconds + NORMAL_MONSTER_MOVE_INTERVAL_SECONDS;
    }
  }

  private moveNormalMonsters(map: V1Map, runtime: V1MapRuntimeState): void {
    const directions = [
      { dx: 0, dy: -1 },
      { dx: 1, dy: 0 },
      { dx: 0, dy: 1 },
      { dx: -1, dy: 0 },
    ];
    const occupied = new Set(runtime.normalMonsters.map((entry) => `${entry.x}:${entry.y}`));
    for (const monster of runtime.normalMonsters) {
      if (this.nextRandom(runtime) >= NORMAL_MONSTER_MOVE_CHANCE) continue;
      occupied.delete(`${monster.x}:${monster.y}`);
      const firstDirection = Math.floor(this.nextRandom(runtime) * directions.length);
      for (let offset = 0; offset < directions.length; offset += 1) {
        const direction = directions[(firstDirection + offset) % directions.length];
        if (!direction) continue;
        const x = monster.x + direction.dx;
        const y = monster.y + direction.dy;
        const cell = map.cells[y]?.[x];
        if (
          !cell
          || cell.terrain !== 0
          || cell.special
          || occupied.has(`${x}:${y}`)
          || (x === this.state.player.x && y === this.state.player.y)
        ) {
          continue;
        }
        monster.x = x;
        monster.y = y;
        break;
      }
      occupied.add(`${monster.x}:${monster.y}`);
    }
  }

  private spawnNormalMonster(map: V1Map, runtime: V1MapRuntimeState): boolean {
    if (map.normalTroops.length === 0) return false;
    const candidates: Array<{ x: number; y: number }> = [];
    const occupied = new Set(runtime.normalMonsters.map((entry) => `${entry.x}:${entry.y}`));
    for (let y = 0; y < map.height; y += 1) {
      for (let x = 0; x < map.width; x += 1) {
        const cell = map.cells[y]?.[x];
        if (!cell || cell.terrain !== 0 || cell.special || occupied.has(`${x}:${y}`)) continue;
        if (map.id === this.state.player.currentMapId) {
          const distance = Math.abs(x - this.state.player.x) + Math.abs(y - this.state.player.y);
          if (distance < this.content.rules.normalSpawnMinDistance) continue;
        }
        candidates.push({ x, y });
      }
    }
    if (candidates.length === 0) return false;
    const totalWeight = map.normalTroops.reduce((sum, entry) => sum + entry.weight, 0);
    let roll = this.nextRandom(runtime) * totalWeight;
    let troopId = map.normalTroops[0]?.troopId ?? 1;
    for (const entry of map.normalTroops) {
      roll -= entry.weight;
      if (roll < 0) {
        troopId = entry.troopId;
        break;
      }
    }
    const position = candidates[Math.floor(this.nextRandom(runtime) * candidates.length)];
    if (!position) return false;
    runtime.normalSequence += 1;
    runtime.normalMonsters.push({
      instanceId: `n:${map.id}:${runtime.normalSequence}`,
      troopId,
      x: position.x,
      y: position.y,
    });
    return true;
  }

  private isWalkable(cell: V1MapCell): boolean {
    return cell.terrain !== 1;
  }

  move(dx: number, dy: number): boolean {
    if (
      !this.state.started ||
      this.state.battle ||
      this.state.loot ||
      this.state.openPanel ||
      this.state.dialogue ||
      this.state.gameOver ||
      Math.abs(dx) + Math.abs(dy) !== 1
    ) {
      return false;
    }
    this.state.player.facingX = dx;
    this.state.player.facingY = dy;
    const map = this.currentMap();
    const x = this.state.player.x + dx;
    const y = this.state.player.y + dy;
    const cell = map.cells[y]?.[x];
    if (!cell || !this.isWalkable(cell)) {
      this.notify("这里无法通行。");
      return false;
    }
    const runtime = this.currentMapRuntime();
    const monster = runtime.normalMonsters.find((entry) => entry.x === x && entry.y === y);
    if (monster) {
      this.startBattle(monster.troopId, monster.instanceId, "normal", true);
      return true;
    }
    if (cell.special?.type === 2) {
      const key = cellKey(map.id, x, y, cell.special.troopId);
      if (!runtime.bosses[key]) {
        this.startBattle(cell.special.troopId, key, "boss", false);
        return true;
      }
    }
    if (cell.special?.type === 3 || cell.special?.type === 4) {
      this.notify("请面向目标按 E 交互。");
      return false;
    }
    if (cell.special?.type === 1) {
      return this.transfer(cell.special);
    }
    this.state.player.x = x;
    this.state.player.y = y;
    this.emit();
    return true;
  }

  private transfer(transfer: Extract<V1MapSpecialLike, { type: 1 }>): boolean {
    if (transfer.requiredItemId) {
      const current = this.state.player.items[transfer.requiredItemId] ?? 0;
      if (current < transfer.quantity) {
        const item = this.content.indexes.items.get(transfer.requiredItemId);
        this.notify(`需要${item?.name ?? "物品"} × ${transfer.quantity}`);
        return false;
      }
    }
    if (this.state.player.gold < transfer.goldCost) {
      this.notify(`金币不足，需要 ${transfer.goldCost.toLocaleString("zh-CN")} 金币`);
      return false;
    }
    const target = this.content.indexes.maps.get(transfer.targetMapId);
    if (!target) return false;
    const entryId = transfer.targetEntryId || target.defaultEntryId;
    const position = this.entryPosition(target.id, entryId);
    if (transfer.requiredItemId && transfer.consume) {
      this.state.player.items[transfer.requiredItemId] =
        (this.state.player.items[transfer.requiredItemId] ?? 0) - transfer.quantity;
    }
    this.state.player.gold -= transfer.goldCost;
    this.state.player.currentMapId = target.id;
    this.state.player.x = position.x;
    this.state.player.y = position.y;
    this.ensureMapRuntime(target);
    this.notify(`进入${target.name}`);
    return true;
  }

  interact(): boolean {
    if (!this.state.started || this.state.battle || this.state.loot || this.state.openPanel || this.state.dialogue) return false;
    const map = this.currentMap();
    const x = this.state.player.x + this.state.player.facingX;
    const y = this.state.player.y + this.state.player.facingY;
    const special = map.cells[y]?.[x]?.special;
    if (special?.type === 3) {
      const npc = map.npcs.find((entry) => entry.id === special.localNpcId);
      if (!npc) return false;
      this.state.dialogue = { speaker: npc.name, lines: npc.dialogue };
      this.emit();
      return true;
    }
    if (special?.type === 4) {
      this.state.activeShopId = special.shopId;
      this.state.openPanel = "shop";
      this.emit();
      return true;
    }
    this.notify("这里没有可交互对象。");
    return false;
  }

  interactAt(targetX: number, targetY: number): boolean {
    const dx = targetX - this.state.player.x;
    const dy = targetY - this.state.player.y;
    if (Math.abs(dx) + Math.abs(dy) !== 1) return false;
    this.state.player.facingX = dx;
    this.state.player.facingY = dy;
    return this.interact();
  }

  closeDialogue(): void {
    this.state.dialogue = null;
    this.emit();
  }

  togglePanel(panel: Exclude<V1Panel, null>): void {
    if (!this.state.started || this.state.battle || this.state.loot || this.state.dialogue) return;
    this.state.openPanel = this.state.openPanel === panel ? null : panel;
    if (this.state.openPanel !== "shop") this.state.activeShopId = null;
    this.emit();
  }

  closePanel(): void {
    this.state.openPanel = null;
    this.state.activeShopId = null;
    this.emit();
  }

  private startBattle(
    troopId: number,
    encounterKey: string,
    kind: "normal" | "boss",
    canEscape: boolean,
  ): void {
    const troop = this.content.indexes.troops.get(troopId);
    if (!troop) throw new Error(`敌群不存在：${troopId}`);
    const battleMembers = kind === "boss"
      ? troop.members
      : this.randomNormalBattleMembers(troop);
    const enemies = battleMembers.map((member, index) => {
      const enemy = this.content.indexes.enemies.get(member.enemyId);
      if (!enemy) throw new Error(`敌群 ${troopId} 引用了未知怪物 ${member.enemyId}`);
      const runtimeStats = this.enemyStats(enemy);
      return {
        index,
        enemyId: enemy.id,
        name: enemy.name,
        hp: runtimeStats.maxHp,
        maxHp: runtimeStats.maxHp,
        spriteSheet: enemy.spriteSheet,
        spriteIndex: enemy.spriteIndex,
        rank: enemy.rank,
      };
    });
    this.state.battle = {
      troopId,
      encounterKey,
      kind,
      enemies,
      selectedIndex: 0,
      round: 1,
      auto: this.content.rules.autoBattlePersist
        ? this.state.autoBattlePreference
        : this.content.rules.autoBattleDefault,
      canEscape: canEscape && troop.canEscape,
      log: [`遭遇${troop.name}。`],
      status: "active",
      autoElapsedMs: 0,
      pendingEnemyTurn: false,
      pendingEnemyMs: 0,
      victoryResolveMs: 0,
      defeatReturnMs: 0,
    };
    this.state.combatFx = null;
    this.emit();
  }

  private randomNormalBattleMembers(troop: V1Troop): V1Troop["members"] {
    const maximum = Math.min(2, troop.members.length);
    const count = maximum === 1
      ? 1
      : 1 + Math.floor(this.randomForCurrentMap() * maximum);
    if (count >= troop.members.length) return [...troop.members];
    const pool = [...troop.members];
    const selected: V1Troop["members"] = [];
    while (selected.length < count && pool.length > 0) {
      const index = Math.floor(this.randomForCurrentMap() * pool.length);
      const [member] = pool.splice(index, 1);
      if (member) selected.push(member);
    }
    return selected;
  }

  selectEnemy(index: number): void {
    const battle = this.state.battle;
    if (!battle || !battle.enemies.some((entry) => entry.index === index && entry.hp > 0)) return;
    battle.selectedIndex = index;
    this.emit();
  }

  setAutoBattle(auto: boolean): void {
    const battle = this.state.battle;
    if (!battle || !this.content.rules.autoBattleEnabled) return;
    battle.auto = auto;
    battle.autoElapsedMs = 0;
    if (this.content.rules.autoBattlePersist) {
      this.state.autoBattlePreference = auto;
    }
    this.emit();
  }

  availableBattleSkills(): V1Skill[] {
    const battle = this.state.battle;
    if (!battle || battle.status !== "active") return [];
    return this.effectivePlayerSkillIds()
      .map((skillId) => this.content.indexes.skills.get(skillId))
      .filter((skill): skill is V1Skill =>
        Boolean(
          skill
          && skill.id !== this.content.rules.basicAttackSkillId
          && skill.type === 1
          && (skill.occasionId === 0 || skill.occasionId === 1)
          && (skill.damageTypeId === 1 || skill.damageTypeId === 5)
          && (skill.formulaCode === 1 || skill.formulaCode === 2)
          && (skill.costType === 0 || skill.costType === 1),
        )
      );
  }

  private equippedGrantedSkillIds(): number[] {
    const result = this.activeSetEffects()
      .map((effect) => effect.split("|"))
      .filter(([code]) => code === "6")
      .map(([, skillId]) => Number(skillId))
      .filter(Number.isSafeInteger);
    for (const instance of Object.values(this.state.player.equipment)) {
      for (const roll of instance.affixRolls) {
        const affix = this.content.indexes.affixes.get(roll.affixId);
        if (affix) result.push(...affix.grantedSkills);
      }
    }
    return [...new Set(result)];
  }

  private equippedAffixes(): V1Affix[] {
    return Object.values(this.state.player.equipment).flatMap((instance) =>
      instance.affixRolls
        .map((roll) => this.content.indexes.affixes.get(roll.affixId))
        .filter((affix): affix is V1Affix => Boolean(affix))
    );
  }

  equipmentBossAbilities(): V1Affix[] {
    const result = new Map<number, V1Affix>();
    for (const affix of this.equippedAffixes()) {
      if (affix.specialPool === 2) result.set(affix.id, affix);
    }
    return [...result.values()];
  }

  equipmentSkillDamageBonusPercent(): number {
    return Math.min(150, this.equipmentBossAbilities().reduce(
      (sum, affix) => sum + affix.skillDamageBonusPercent,
      0,
    ));
  }

  private rollEquipmentProcDamage(damage: number): { damage: number; names: string[] } {
    let procDamage = 0;
    const names: string[] = [];
    for (const affix of this.equipmentBossAbilities()) {
      if (affix.procChancePercent <= 0 || affix.procDamagePercent <= 0) continue;
      if (this.randomForCurrentMap() * 100 >= affix.procChancePercent) continue;
      procDamage += Math.max(1, Math.floor(damage * affix.procDamagePercent / 100));
      names.push(affix.name);
    }
    return {
      damage: Math.min(procDamage, Math.floor(damage * 2)),
      names,
    };
  }

  equipmentGrantedSkills(): V1Skill[] {
    return this.equippedGrantedSkillIds()
      .map((skillId) => this.content.indexes.skills.get(skillId))
      .filter((skill): skill is V1Skill => Boolean(skill));
  }

  private effectivePlayerSkillIds(): number[] {
    return [...new Set([
      ...this.state.player.learnedSkills,
      ...this.equippedGrantedSkillIds(),
    ])];
  }

  battleSkillCost(skillId: number): number {
    const skill = this.content.indexes.skills.get(skillId);
    return Math.max(0, skill?.costs[0] ?? 0);
  }

  canUseBattleSkill(skillId: number): boolean {
    const battle = this.state.battle;
    const skill = this.availableBattleSkills().find((entry) => entry.id === skillId);
    if (!battle || !skill || battle.pendingEnemyTurn) return false;
    return skill.costType !== 1 || this.state.player.mp >= this.battleSkillCost(skillId);
  }

  private traitsForPassiveStates(stateIds: readonly number[]): V1Trait[] {
    return stateIds.flatMap((stateId) =>
      this.content.states.find((state) => state.id === stateId)?.traits ?? []
    );
  }

  private activeSetEffects(): string[] {
    const equippedIds = new Set(
      Object.values(this.state.player.equipment).map((instance) => instance.equipmentId),
    );
    const effects: string[] = [];
    for (const set of this.content.sets) {
      const count = set.memberEquipment.filter((equipmentId) => equippedIds.has(equipmentId)).length;
      for (let threshold = 2; threshold <= count; threshold += 1) {
        effects.push(...(set.bonuses[threshold] ?? []));
      }
    }
    return effects;
  }

  private playerTraits(): V1Trait[] {
    const traits = [
      ...this.content.actor.traits,
      ...this.traitsForPassiveStates(this.content.actor.passiveStates),
    ];
    for (const skillId of this.effectivePlayerSkillIds()) {
      const skill = this.content.indexes.skills.get(skillId);
      if (skill?.type === 2) {
        traits.push(...skill.traits, ...this.traitsForPassiveStates(skill.passiveStates));
      }
    }
    for (const instance of Object.values(this.state.player.equipment)) {
      const base = this.content.indexes.equipment.get(instance.equipmentId);
      if (!base) continue;
      traits.push(...base.traits, ...this.traitsForPassiveStates(base.passiveStates));
      for (const roll of instance.affixRolls) {
        const affix = this.content.indexes.affixes.get(roll.affixId);
        if (affix) traits.push(...affix.traits, ...this.traitsForPassiveStates(affix.passiveStates));
      }
    }
    const passiveStateIds = this.activeSetEffects()
      .map((effect) => effect.split("|"))
      .filter(([code]) => code === "4")
      .map(([, stateId]) => Number(stateId))
      .filter(Number.isSafeInteger);
    traits.push(...this.traitsForPassiveStates(passiveStateIds));
    return traits;
  }

  private playerExperienceRate(): number {
    return this.playerTraits()
      .filter((trait) => trait.code === 23 && trait.dataId === 9)
      .reduce((rate, trait) => rate * trait.value, 1);
  }

  private lifeStealForStates(stateIds: readonly number[]): V1LifeSteal[] {
    return stateIds.flatMap((stateId) =>
      this.content.states.find((state) => state.id === stateId)?.lifeSteal ?? []
    );
  }

  private playerStealTotals(): {
    hp: { rate: number; flat: number };
    mp: { rate: number; flat: number };
  } {
    const mechanics = [
      ...this.lifeStealForStates(this.content.actor.passiveStates),
    ];
    for (const skillId of this.effectivePlayerSkillIds()) {
      const skill = this.content.indexes.skills.get(skillId);
      if (skill?.type === 2) mechanics.push(...this.lifeStealForStates(skill.passiveStates));
    }
    for (const instance of Object.values(this.state.player.equipment)) {
      const base = this.content.indexes.equipment.get(instance.equipmentId);
      if (!base) continue;
      mechanics.push(
        ...base.lifeSteal,
        ...this.lifeStealForStates(base.passiveStates),
      );
      for (const roll of instance.affixRolls) {
        const affix = this.content.indexes.affixes.get(roll.affixId);
        if (!affix) continue;
        mechanics.push(
          ...affix.lifeSteal,
          ...this.lifeStealForStates(affix.passiveStates),
        );
      }
    }
    const setStateIds = this.activeSetEffects()
      .map((effect) => effect.split("|"))
      .filter(([code]) => code === "4")
      .map(([, stateId]) => Number(stateId))
      .filter(Number.isSafeInteger);
    mechanics.push(...this.lifeStealForStates(setStateIds));
    return {
      hp: lifeStealTotals(mechanics, 1),
      mp: lifeStealTotals(mechanics, 2),
    };
  }

  private enemyLifeSteal(enemy: V1Enemy): { rate: number; flat: number } {
    return lifeStealTotals([
      ...enemy.lifeSteal,
      ...this.lifeStealForStates(enemy.passiveStates),
    ], 1);
  }

  private enemyTraits(enemy: V1Enemy): V1Trait[] {
    return [...enemy.traits, ...this.traitsForPassiveStates(enemy.passiveStates)];
  }

  private enemyStats(enemy: V1Enemy): V1Stats {
    return v1ApplyParameterRates(enemy, this.enemyTraits(enemy));
  }

  private enemyAction(skillActions: V1Enemy["actions"], battle: V1BattleState, hpRate: number): V1Skill | null {
    const currentTurn = Math.max(0, battle.round - 1);
    const valid = skillActions.filter((action) => {
      if (action.conditionType === 1) {
        const interval = action.param2;
        return interval === 0
          ? currentTurn === action.param1
          : currentTurn > 0
            && currentTurn >= action.param1
            && currentTurn % interval === action.param1 % interval;
      }
      if (action.conditionType === 2) return hpRate >= action.param1 && hpRate <= action.param2;
      return action.conditionType === 0;
    });
    if (valid.length === 0) return null;
    const ratingMax = Math.max(...valid.map((action) => action.weight));
    const ratingZero = ratingMax - 3;
    const weighted = valid.filter((action) => action.weight > ratingZero);
    let roll = Math.floor(
      this.randomForCurrentMap()
      * weighted.reduce((sum, action) => sum + action.weight - ratingZero, 0),
    );
    let selected = weighted[weighted.length - 1]!;
    for (const action of weighted) {
      roll -= action.weight - ratingZero;
      if (roll < 0) {
        selected = action;
        break;
      }
    }
    return this.content.indexes.skills.get(selected.skillId) ?? null;
  }

  performAttack(): boolean {
    return this.performBattleSkillInternal(
      this.content.rules.basicAttackSkillId,
      true,
    );
  }

  performBattleSkill(skillId: number): boolean {
    return this.performBattleSkillInternal(skillId, false);
  }

  performAutoBattleAction(): boolean {
    const battle = this.state.battle;
    if (!battle || battle.status !== "active" || battle.pendingEnemyTurn) return false;
    const target = battle.enemies.find(
      (entry) => entry.index === battle.selectedIndex && entry.hp > 0,
    ) ?? battle.enemies.find((entry) => entry.hp > 0);
    if (!target) return false;
    const targetEnemy = this.content.indexes.enemies.get(target.enemyId);
    const player = v1RuntimeBattler(this.playerStats(), this.playerTraits());
    const targetRuntime = targetEnemy
      ? v1RuntimeBattler(this.enemyStats(targetEnemy), this.enemyTraits(targetEnemy))
      : null;
    const livingEnemyCount = battle.enemies.filter((entry) => entry.hp > 0).length;
    const candidates = this.availableBattleSkills()
      .filter((skill) => this.canUseBattleSkill(skill.id))
      .map((skill) => {
        const damage = targetRuntime
          ? Math.max(0, v1SkillBaseDamage(skill, 0, player, targetRuntime))
          : 0;
        const targetCount = skill.scopeId === 2 ? livingEnemyCount : 1;
        return {
          skill,
          score: damage * Math.max(1, skill.repeats) * targetCount,
          cost: this.battleSkillCost(skill.id),
        };
      })
      .sort((left, right) =>
        right.score - left.score
        || left.cost - right.cost
        || left.skill.id - right.skill.id
      );
    const selected = candidates[0]?.skill;
    return selected ? this.performBattleSkill(selected.id) : this.performAttack();
  }

  private performBattleSkillInternal(
    skillId: number,
    forceSingleTarget: boolean,
  ): boolean {
    const battle = this.state.battle;
    if (!battle || battle.status !== "active" || battle.pendingEnemyTurn) return false;
    const selectedTarget = battle.enemies.find((entry) => entry.index === battle.selectedIndex && entry.hp > 0)
      ?? battle.enemies.find((entry) => entry.hp > 0);
    const isBasicAttack = skillId === this.content.rules.basicAttackSkillId;
    const skill = isBasicAttack
      ? this.content.indexes.skills.get(skillId)
      : this.availableBattleSkills().find((entry) => entry.id === skillId);
    if (!selectedTarget || !skill) return false;
    const cost = this.battleSkillCost(skill.id);
    if (skill.costType === 1) {
      if (this.state.player.mp < cost) return false;
      this.state.player.mp -= cost;
    } else if (skill.costType !== 0) {
      return false;
    }
    const playerStats = this.playerStats();
    const player = v1RuntimeBattler(playerStats, this.playerTraits());
    const playerSteal = this.playerStealTotals();
    const skillDamageBonusPercent = isBasicAttack ? 0 : this.equipmentSkillDamageBonusPercent();
    const targets = !forceSingleTarget && skill.scopeId === 2
      ? battle.enemies.filter((entry) => entry.hp > 0)
      : [selectedTarget];
    let totalDamage = 0;
    let selectedDamage = 0;
    let anyCritical = false;
    let landed = false;
    let restoredHp = 0;
    let restoredMp = 0;
    let totalProcDamage = 0;
    const triggeredProcNames = new Set<string>();
    for (let repeat = 0; repeat < Math.max(1, skill.repeats); repeat += 1) {
      for (const target of targets) {
        if (target.hp <= 0) continue;
        const enemy = this.content.indexes.enemies.get(target.enemyId);
        if (!enemy) continue;
        const outcome = rollV1SkillOutcome(
          skill,
          0,
          player,
          v1RuntimeBattler(this.enemyStats(enemy), this.enemyTraits(enemy)),
          this.content.rules,
          () => this.randomForCurrentMap(),
        );
        landed ||= outcome.landed;
        anyCritical ||= outcome.critical;
        const skillDamage = skillDamageBonusPercent > 0
          ? Math.round(outcome.damage * (100 + skillDamageBonusPercent) / 100)
          : outcome.damage;
        target.hp = Math.max(0, target.hp - skillDamage);
        const proc = skillDamage > 0 && target.hp > 0
          ? this.rollEquipmentProcDamage(skillDamage)
          : { damage: 0, names: [] };
        const appliedProcDamage = Math.min(target.hp, proc.damage);
        target.hp = Math.max(0, target.hp - appliedProcDamage);
        const combinedDamage = skillDamage + appliedProcDamage;
        totalDamage += combinedDamage;
        totalProcDamage += appliedProcDamage;
        proc.names.forEach((name) => triggeredProcNames.add(name));
        if (target.index === selectedTarget.index) selectedDamage += combinedDamage;
        if (skill.hitTypeId === 1 && combinedDamage > 0) {
          const healing = lifeStealForDamage(combinedDamage, playerSteal.hp);
          const mana = lifeStealForDamage(combinedDamage, playerSteal.mp);
          const hpBefore = this.state.player.hp;
          const mpBefore = this.state.player.mp;
          this.state.player.hp = Math.min(playerStats.maxHp, hpBefore + healing);
          this.state.player.mp = Math.min(playerStats.maxMp, mpBefore + mana);
          restoredHp += this.state.player.hp - hpBefore;
          restoredMp += this.state.player.mp - mpBefore;
        }
        if (target.hp === 0) battle.log.unshift(`${target.name}被击败。`);
      }
    }
    this.state.combatSequence += 1;
    this.state.combatFx = {
      sequence: this.state.combatSequence,
      animationId: skill.animationId,
      targetIndex: selectedTarget.index,
      amount: selectedDamage,
      durationMs: PLAYER_COMBAT_FX_MS,
      kind: "damage",
    };
    battle.log.unshift(
      landed
        ? `${this.content.actor.name}${anyCritical ? "暴击，" : ""}以${skill.name}造成 ${totalDamage} 点伤害${skillDamageBonusPercent > 0 ? `，技能光环增伤 ${skillDamageBonusPercent}%` : ""}${totalProcDamage > 0 ? `，${[...triggeredProcNames].join("、")}触发追击 ${totalProcDamage} 点` : ""}${restoredHp > 0 ? `，吸血恢复 ${restoredHp} 点生命` : ""}${restoredMp > 0 ? `，吸蓝恢复 ${restoredMp} 点魔法` : ""}。`
        : `${this.content.actor.name}的${skill.name}未命中。`,
    );
    const alive = battle.enemies.filter((entry) => entry.hp > 0);
    if (alive.length === 0) {
      battle.status = "victory";
      battle.victoryResolveMs = VICTORY_RESOLVE_DELAY_MS;
      battle.log.unshift("战斗胜利，正在结算战利品。");
      this.emit();
      return true;
    }
    battle.selectedIndex = alive[0]?.index ?? 0;
    battle.pendingEnemyTurn = true;
    battle.pendingEnemyMs = 1_050;
    this.emit();
    return true;
  }

  private resolveEnemyTurn(battle: V1BattleState): void {
    if (this.state.battle !== battle || battle.status !== "active") return;
    battle.pendingEnemyTurn = false;
    const player = v1RuntimeBattler(this.playerStats(), this.playerTraits());
    let received = 0;
    let animationId = this.content.indexes.skills.get(this.content.rules.basicAttackSkillId)?.animationId ?? 1;
    let actingEnemyName = "敌方";
    let enemyRestoredHp = 0;
    for (const unit of battle.enemies.filter((entry) => entry.hp > 0)) {
      const attacker = this.content.indexes.enemies.get(unit.enemyId);
      if (!attacker) continue;
      const enemySkill = this.enemyAction(attacker.actions, battle, unit.hp / unit.maxHp);
      if (!enemySkill) continue;
      animationId = enemySkill.animationId;
      actingEnemyName = attacker.name;
      const outcome = rollV1SkillOutcome(
        enemySkill,
        0,
        v1RuntimeBattler(this.enemyStats(attacker), this.enemyTraits(attacker)),
        player,
        this.content.rules,
        () => this.randomForCurrentMap(),
      );
      if (outcome.landed && enemySkill.damageTypeId !== 0) {
        received += Math.min(this.state.player.hp, outcome.damage);
        const healing = enemySkill.hitTypeId === 1
          ? lifeStealForDamage(outcome.damage, this.enemyLifeSteal(attacker))
          : enemySkill.damageTypeId === 5
            ? outcome.damage
            : 0;
        const hpBefore = unit.hp;
        unit.hp = Math.min(unit.maxHp, unit.hp + healing);
        enemyRestoredHp += unit.hp - hpBefore;
      }
    }
    this.state.player.hp = Math.max(0, this.state.player.hp - received);
    battle.log.unshift(received > 0
      ? `敌方行动造成 ${received} 点伤害${enemyRestoredHp > 0 ? `，吸血恢复 ${enemyRestoredHp} 点生命` : ""}。`
      : "敌方行动未造成伤害。");
    this.state.combatSequence += 1;
    this.state.combatFx = {
      sequence: this.state.combatSequence,
      animationId,
      targetIndex: -1,
      amount: received,
      durationMs: 950,
      kind: "damage",
    };
    battle.round += 1;
    if (this.state.player.hp <= 0) {
      battle.status = "defeat";
      battle.defeatReturnMs = 2_000;
      battle.log.unshift(`${actingEnemyName}击败了${this.content.actor.name}，2 秒后回城。`);
    }
    this.emit();
  }

  flee(): boolean {
    const battle = this.state.battle;
    if (!battle || !battle.canEscape || battle.status !== "active") return false;
    this.state.battle = null;
    this.notify("成功撤离战斗。");
    return true;
  }

  private resolveVictory(battle: V1BattleState): void {
    const runtime = this.currentMapRuntime();
    if (battle.kind === "normal") {
      runtime.normalMonsters = runtime.normalMonsters.filter((entry) => entry.instanceId !== battle.encounterKey);
      runtime.normalRespawnDueAt.push(this.state.gameSeconds + this.currentMap().normalRespawnSeconds);
      runtime.normalRespawnDueAt.sort((left, right) => left - right);
    } else {
      const boss = this.bossAtEncounterKey(battle.encounterKey);
      const delay = boss?.respawnSeconds ?? this.currentMap().bossRespawnSeconds;
      runtime.bosses[battle.encounterKey] = {
        defeatedAt: this.state.gameSeconds,
        nextRespawnAt: delay > 0 ? this.state.gameSeconds + delay : null,
      };
    }
    const defeated = battle.enemies.map((unit) => this.content.indexes.enemies.get(unit.enemyId)).filter((enemy): enemy is V1Enemy => Boolean(enemy));
    const equipment: V1EquipmentInstance[] = [];
    const items = new Map<number, number>();
    let exp = 0;
    let gold = 0;
    const dropRate = this.dropRateMultiplier();
    for (const enemy of defeated) {
      exp += enemy.exp;
      gold += enemy.gold;
      for (const drop of enemy.drops) {
        if (this.randomForCurrentMap() * 100 >= drop.chancePercent * dropRate) continue;
        if (drop.kind === 1) items.set(drop.entryId, (items.get(drop.entryId) ?? 0) + 1);
        else {
          const profileId = battle.kind === "boss"
            ? this.currentMap().bossQualityProfileId
            : this.currentMap().normalQualityProfileId;
          equipment.push(this.rollEquipmentInstance(drop.entryId, profileId));
        }
      }
    }
    if (battle.kind === "boss") {
      for (const enemy of defeated.filter((entry) => entry.rank === 3)) {
        const exclusive = this.content.indexes.bossLoot.get(enemy.id);
        if (!exclusive || this.randomForCurrentMap() * 100 >= exclusive.dropChancePercent * dropRate) {
          continue;
        }
        const equipmentId = exclusive.equipmentPool[
          Math.floor(this.randomForCurrentMap() * exclusive.equipmentPool.length)
        ];
        const affixId = exclusive.abilityAffixPool[
          Math.floor(this.randomForCurrentMap() * exclusive.abilityAffixPool.length)
        ];
        if (equipmentId === undefined || affixId === undefined) continue;
        const instance = this.rollEquipmentInstance(
          equipmentId,
          this.currentMap().bossQualityProfileId,
          exclusive.minimumQualityId,
        );
        instance.bossEnemyId = enemy.id;
        instance.affixRolls.push({ lineNo: 99, affixId, randomValue: null });
        equipment.push(instance);
      }
    }
    exp = Math.floor(exp * this.playerExperienceRate());
    const firstBossDefeated = battle.kind === "boss" && !this.state.firstBossDefeated;
    this.state.firstBossDefeated ||= battle.kind === "boss";
    this.state.loot = {
      exp,
      gold,
      items: [...items.entries()].map(([itemId, quantity]) => ({ itemId, quantity })),
      equipment,
      sourceKind: battle.kind,
      firstBossDefeated,
    };
    this.state.battle = null;
    this.emit();
  }

  private resolveDefeat(): void {
    const stats = this.playerStats();
    const position = this.entryPosition(this.content.rules.deathMapId, this.content.rules.deathEntryId);
    this.state.player.currentMapId = this.content.rules.deathMapId;
    this.state.player.x = position.x;
    this.state.player.y = position.y;
    this.state.player.hp = stats.maxHp;
    this.state.player.mp = stats.maxMp;
    this.state.battle = null;
    this.notify("战败后回到起居室，状态已恢复。");
  }

  collectLoot(): void {
    const loot = this.state.loot;
    if (!loot) return;
    const keptEquipment: V1EquipmentInstance[] = [];
    let autoSoldCount = 0;
    let autoSoldGold = 0;
    for (const instance of loot.equipment) {
      if (!this.shouldAutoSellLootEquipment(instance)) {
        keptEquipment.push(instance);
        continue;
      }
      autoSoldCount += 1;
      autoSoldGold += this.equipmentSalePrice(instance);
    }
    this.state.player.gold += loot.gold + autoSoldGold;
    for (const item of loot.items) {
      this.state.player.items[item.itemId] = Math.min(
        this.content.rules.itemStackLimit,
        (this.state.player.items[item.itemId] ?? 0) + item.quantity,
      );
    }
    this.state.player.inventory.push(...keptEquipment);
    this.state.player.exp += loot.exp;
    const oldLevel = this.state.player.level;
    while (this.state.player.level < this.content.rules.maxLevel) {
      const next = this.content.indexes.levels.get(this.state.player.level + 1);
      if (!next || this.state.player.exp < next.expRequired) break;
      this.state.player.level += 1;
      for (const learned of next.learnSkills) {
        if (!this.state.player.learnedSkills.includes(learned.skillId)) this.state.player.learnedSkills.push(learned.skillId);
      }
    }
    if (this.state.player.level > oldLevel && this.content.rules.levelUpRecoverAll) {
      const stats = this.playerStats();
      this.state.player.hp = stats.maxHp;
      this.state.player.mp = stats.maxMp;
    }
    const itemQuantity = loot.items.reduce((sum, entry) => sum + entry.quantity, 0);
    const rewardSummary = [
      `${loot.exp} 经验`,
      `${loot.gold} 金币`,
      keptEquipment.length > 0 ? `${keptEquipment.length} 件装备` : "",
      autoSoldCount > 0 ? `自动出售 ${autoSoldCount} 件装备，另得 ${autoSoldGold} 金币` : "",
      itemQuantity > 0 ? `${itemQuantity} 件物品` : "",
    ].filter(Boolean).join("、");
    const message = loot.firstBossDefeated
      ? `首领铁牙狼王已击败！获得 ${rewardSummary}，第一阶段试玩目标完成。`
      : `获得 ${rewardSummary}。`;
    this.state.loot = null;
    this.notify(message);
  }

  setAutoSellMaxQualityId(qualityId: number): void {
    this.state.autoSellMaxQualityId = clamp(Math.floor(qualityId), 0, 5);
    const qualityName = this.state.autoSellMaxQualityId === 0
      ? "关闭"
      : this.content.dictionary.get("quality")?.get(this.state.autoSellMaxQualityId)
        ?? `品质 ${this.state.autoSellMaxQualityId}`;
    this.notify(
      this.state.autoSellMaxQualityId === 0
        ? "战利品自动出售已关闭。"
        : `将自动出售 ${qualityName} 及以下品质装备；极品与更高品质会保留。`,
    );
  }

  cycleAutoSellMaxQualityId(): void {
    this.setAutoSellMaxQualityId((this.state.autoSellMaxQualityId + 1) % 6);
  }

  lootAutoSaleQuote(): { count: number; gold: number } {
    let count = 0;
    let gold = 0;
    for (const instance of this.state.loot?.equipment ?? []) {
      if (!this.shouldAutoSellLootEquipment(instance)) continue;
      count += 1;
      gold += this.equipmentSalePrice(instance);
    }
    return { count, gold };
  }

  willAutoSellLootEquipment(instance: V1EquipmentInstance): boolean {
    return this.shouldAutoSellLootEquipment(instance);
  }

  private shouldAutoSellLootEquipment(instance: V1EquipmentInstance): boolean {
    if (this.state.autoSellMaxQualityId <= 0 || this.isExceptionalEquipment(instance)) {
      return false;
    }
    const base = this.content.indexes.equipment.get(instance.equipmentId);
    if (!base?.canSell || this.equipmentSalePrice(instance) <= 0) return false;
    return this.deriveEquipment(instance).qualityId <= this.state.autoSellMaxQualityId;
  }

  private randomForCurrentMap(): number {
    return this.nextRandom(this.currentMapRuntime());
  }

  rollEquipmentInstance(
    equipmentId: number,
    qualityProfileId?: number,
    minimumQualityId = 1,
  ): V1EquipmentInstance {
    const base = this.content.indexes.equipment.get(equipmentId);
    if (!base) throw new Error(`掉落装备不存在：${equipmentId}`);
    const profile = this.resolveQualityProfile(qualityProfileId);
    const rolls: V1AffixRoll[] = [];
    const qualityId = Math.max(minimumQualityId, this.rollQualityId(profile));
    if (qualityId > 1) {
      const qualityAffixes = this.content.affixes.filter((affix) => affix.qualityId === qualityId);
      const affix = qualityAffixes[Math.floor(this.randomForCurrentMap() * qualityAffixes.length)];
      const range = profile.pointRanges[qualityId];
      if (!affix || !range) throw new Error(`品质档案 ${profile.id} 缺少品质 ${qualityId} 的词缀或点数区间`);
      const randomValue = range.min + Math.floor(this.randomForCurrentMap() * (range.max - range.min + 1));
      rolls.push({
        lineNo: 1,
        affixId: affix.id,
        randomValue,
        statAllocations: this.rollQualityStatAllocations(randomValue),
      });
    }
    const perkChance = qualityId >= 6
      ? this.content.rules.mythicPerkChancePercent
      : qualityId >= 5
        ? this.content.rules.legendaryPerkChancePercent
        : 0;
    if (perkChance > 0 && this.randomForCurrentMap() * 100 < perkChance) {
      const perks = this.content.affixes.filter((affix) =>
        affix.specialPool === 1 && affix.minimumQualityId <= qualityId
      );
      const perk = perks[Math.floor(this.randomForCurrentMap() * perks.length)];
      if (perk) rolls.push({ lineNo: 50, affixId: perk.id, randomValue: null });
    }
    for (const rule of [...base.affixRules].sort((left, right) => left.lineNo - right.lineNo)) {
      const candidates = rule.candidates.filter((candidate) =>
        this.content.indexes.affixes.get(candidate.affixId)?.qualityId === null
      );
      const chancePercent = Math.min(100, rule.chancePercent * profile.exceptionalChanceMultiplier);
      const lineRoll = this.randomForCurrentMap() * 100;
      if (candidates.length === 0 || lineRoll >= chancePercent) continue;
      const totalWeight = candidates.reduce((sum, candidate) => sum + candidate.weight, 0);
      let candidateRoll = this.randomForCurrentMap() * totalWeight;
      let selected = candidates[candidates.length - 1]!;
      for (const candidate of candidates) {
        candidateRoll -= candidate.weight;
        if (candidateRoll < 0) {
          selected = candidate;
          break;
        }
      }
      const affixId = selected.affixId;
      const affix = this.content.indexes.affixes.get(affixId);
      if (!affix) continue;
      const randomValue = affix.randomMin !== null && affix.randomMax !== null
        ? affix.randomMin + Math.floor(this.randomForCurrentMap() * (affix.randomMax - affix.randomMin + 1))
        : null;
      const statAllocations = affix.qualityId !== null && randomValue !== null
        ? this.rollQualityStatAllocations(randomValue)
        : undefined;
      rolls.push({ lineNo: rule.lineNo, affixId, randomValue, statAllocations });
    }
    this.expandExceptionalAffixes(base, rolls);
    this.state.equipmentSequence += 1;
    return {
      instanceId: `e:${this.state.equipmentSequence}`,
      equipmentId,
      affixRolls: rolls,
      locked: false,
    };
  }

  private resolveQualityProfile(qualityProfileId?: number): V1QualityProfile {
    const profileId = qualityProfileId ?? this.currentMap().normalQualityProfileId;
    const profile = this.content.indexes.qualityProfiles.get(profileId);
    if (!profile) throw new Error(`品质档案不存在：${profileId}`);
    return profile;
  }

  private rollQualityId(profile: V1QualityProfile): number {
    let roll = this.randomForCurrentMap() * 100;
    for (let index = 0; index < profile.qualityChances.length; index += 1) {
      roll -= profile.qualityChances[index] ?? 0;
      if (roll < 0) return index + 1;
    }
    return 6;
  }

  private rollQualityStatAllocations(
    totalPoints: number,
  ): Array<{ statCode: number; value: number }> {
    const statPool = [2, 3, 4, 5, 7];
    const maximum = Math.min(3, statPool.length, Math.max(1, totalPoints));
    const count = 1 + Math.floor(this.randomForCurrentMap() * maximum);
    const selected: number[] = [];
    while (selected.length < count) {
      const index = Math.floor(this.randomForCurrentMap() * statPool.length);
      const [statCode] = statPool.splice(index, 1);
      if (statCode !== undefined) selected.push(statCode);
    }
    let remaining = totalPoints;
    return selected.map((statCode, index) => {
      const remainingSlots = selected.length - index - 1;
      const value = remainingSlots === 0
        ? remaining
        : 1 + Math.floor(this.randomForCurrentMap() * (remaining - remainingSlots));
      remaining -= value;
      return { statCode, value };
    });
  }

  private expandExceptionalAffixes(
    base: V1Equipment,
    rolls: V1AffixRoll[],
  ): void {
    const exceptionalRolls = rolls.filter((roll) =>
      this.content.indexes.affixes.get(roll.affixId)?.qualityId === null
    );
    if (exceptionalRolls.length === 0) return;
    const targetCount = 1 + Math.floor(this.randomForCurrentMap() * 3);
    const usedAffixIds = new Set(rolls.map((roll) => roll.affixId));
    const usedLines = new Set(exceptionalRolls.map((roll) => roll.lineNo));
    while (exceptionalRolls.length < targetCount) {
      const availableRules = base.affixRules.filter((rule) =>
        !usedLines.has(rule.lineNo)
        && rule.candidates.some((candidate) => {
          const affix = this.content.indexes.affixes.get(candidate.affixId);
          return affix?.qualityId === null && !usedAffixIds.has(candidate.affixId);
        })
      );
      if (availableRules.length === 0) break;
      const rule = availableRules[Math.floor(this.randomForCurrentMap() * availableRules.length)]!;
      const candidates = rule.candidates.filter((candidate) => {
        const affix = this.content.indexes.affixes.get(candidate.affixId);
        return affix?.qualityId === null && !usedAffixIds.has(candidate.affixId);
      });
      const totalWeight = candidates.reduce((sum, candidate) => sum + candidate.weight, 0);
      let candidateRoll = this.randomForCurrentMap() * totalWeight;
      let selected = candidates[candidates.length - 1];
      for (const candidate of candidates) {
        candidateRoll -= candidate.weight;
        if (candidateRoll < 0) {
          selected = candidate;
          break;
        }
      }
      if (!selected) break;
      const affix = this.content.indexes.affixes.get(selected.affixId);
      if (!affix) break;
      const randomValue = affix.randomMin !== null && affix.randomMax !== null
        ? affix.randomMin + Math.floor(this.randomForCurrentMap() * (affix.randomMax - affix.randomMin + 1))
        : null;
      const added: V1AffixRoll = {
        lineNo: rule.lineNo,
        affixId: selected.affixId,
        randomValue,
      };
      rolls.push(added);
      exceptionalRolls.push(added);
      usedAffixIds.add(selected.affixId);
      usedLines.add(rule.lineNo);
    }
  }

  deriveEquipment(instance: V1EquipmentInstance): V1DerivedEquipment {
    const base = this.content.indexes.equipment.get(instance.equipmentId);
    if (!base) throw new Error(`装备实例底板不存在：${instance.equipmentId}`);
    const result = emptyStats();
    addStats(result, base);
    let qualityId = base.qualityId;
    let finalPrice = base.buyPrice;
    for (const roll of instance.affixRolls) {
      const affix = this.content.indexes.affixes.get(roll.affixId);
      if (!affix) throw new Error(`装备实例词缀不存在：${roll.affixId}`);
      addStats(result, affix);
      if (roll.statAllocations && roll.statAllocations.length > 0) {
        for (const allocation of roll.statAllocations) {
          const key = statByCode(result, allocation.statCode);
          if (key) result[key] += allocation.value;
        }
      } else if (affix.randomStatCode !== null && roll.randomValue !== null) {
        const key = statByCode(result, affix.randomStatCode);
        if (key) result[key] += roll.randomValue;
      }
      if (roll.randomValue !== null) {
        finalPrice += roll.randomValue * (affix.randomPricePerPoint ?? 0);
      }
      finalPrice += affix.priceDelta;
      if (affix.qualityId !== null) qualityId = Math.max(qualityId, affix.qualityId);
    }
    return {
      ...instance,
      name: instance.bossEnemyId
        ? `【${this.content.indexes.enemies.get(instance.bossEnemyId)?.name ?? "Boss"}专属】${base.name}`
        : base.name,
      qualityId,
      finalPrice,
      stats: result,
    };
  }

  compareEquipment(instanceId: string): V1EquipmentComparison | null {
    const instance = this.state.player.inventory.find((entry) => entry.instanceId === instanceId);
    if (!instance) return null;
    const base = this.content.indexes.equipment.get(instance.equipmentId);
    if (!base) return null;
    const candidateSlots = v1EquipmentSlotsForType(base.equipmentType);
    const target = candidateSlots.find((slot) => !this.state.player.equipment[slot.slotId])
      ?? candidateSlots[candidateSlots.length - 1];
    if (!target) return null;
    const candidate = this.deriveEquipment(instance);
    const currentInstance = this.state.player.equipment[target.slotId];
    const current = currentInstance ? this.deriveEquipment(currentInstance) : null;
    const currentStats = current?.stats ?? emptyStats();
    const deltas = emptyStats();
    for (const key of STAT_KEYS) deltas[key] = candidate.stats[key] - currentStats[key];
    return { targetSlotId: target.slotId, current, deltas };
  }

  isExceptionalEquipment(
    instance: V1EquipmentInstance | V1DerivedEquipment,
  ): boolean {
    return instance.affixRolls.some((roll) => {
      const affix = this.content.indexes.affixes.get(roll.affixId);
      return affix?.qualityId === null;
    });
  }

  equip(instanceId: string, targetSlotId?: number): boolean {
    const index = this.state.player.inventory.findIndex((entry) => entry.instanceId === instanceId);
    if (index < 0) return false;
    const instance = this.state.player.inventory[index];
    if (!instance) return false;
    const base = this.content.indexes.equipment.get(instance.equipmentId);
    if (!base || this.state.player.level < base.requiredLevel) return false;
    const candidateSlots = v1EquipmentSlotsForType(base.equipmentType);
    if (candidateSlots.length === 0) return false;
    const slot = targetSlotId === undefined
      ? candidateSlots.find((candidate) => !this.state.player.equipment[candidate.slotId])
          ?? candidateSlots[candidateSlots.length - 1]
      : candidateSlots.find((candidate) => candidate.slotId === targetSlotId);
    if (!slot) return false;
    const previous = this.state.player.equipment[slot.slotId];
    this.state.player.inventory.splice(index, 1);
    if (previous) this.state.player.inventory.push(previous);
    this.state.player.equipment[slot.slotId] = instance;
    const stats = this.playerStats();
    this.state.player.hp = Math.min(stats.maxHp, this.state.player.hp + Math.max(0, base.maxHp));
    this.state.player.mp = Math.min(stats.maxMp, this.state.player.mp + Math.max(0, base.maxMp));
    this.notify(`已装备 ${this.deriveEquipment(instance).name}`);
    return true;
  }

  unequip(equipmentSlotId: number): boolean {
    const instance = this.state.player.equipment[equipmentSlotId];
    if (!instance) return false;
    const base = this.content.indexes.equipment.get(instance.equipmentId);
    if (!base?.canRemove) return false;
    delete this.state.player.equipment[equipmentSlotId];
    this.state.player.inventory.push(instance);
    const stats = this.playerStats();
    this.state.player.hp = Math.min(this.state.player.hp, stats.maxHp);
    this.state.player.mp = Math.min(this.state.player.mp, stats.maxMp);
    this.notify(`已卸下 ${this.deriveEquipment(instance).name}`);
    return true;
  }

  equipmentSalePrice(
    instance: V1EquipmentInstance | V1DerivedEquipment,
  ): number {
    const derived =
      "finalPrice" in instance ? instance : this.deriveEquipment(instance);
    return Math.max(
      0,
      Math.round(derived.finalPrice * this.content.rules.defaultSellPercent / 100),
    );
  }

  dropRateMultiplier(luck = this.playerStats().luck): number {
    if (this.content.rules.dropFormulaCode === 2) {
      return Math.min(1.5, 1 + Math.max(0, luck) / 500);
    }
    return Math.max(0, (100 + luck) / 100);
  }

  sellEquipment(instanceId: string): boolean {
    const result = this.sellEquipmentBatch([instanceId]);
    return result.count === 1;
  }

  sellEquipmentBatch(instanceIds: readonly string[]): {
    count: number;
    gold: number;
  } {
    const selected = new Set(instanceIds);
    let count = 0;
    let gold = 0;
    this.state.player.inventory = this.state.player.inventory.filter(
      (instance) => {
        if (!selected.has(instance.instanceId) || instance.locked) return true;
        const base = this.content.indexes.equipment.get(instance.equipmentId);
        if (!base?.canSell) return true;
        const value = this.equipmentSalePrice(instance);
        if (value <= 0) return true;
        count += 1;
        gold += value;
        return false;
      },
    );
    if (count > 0) {
      this.state.player.gold += gold;
      this.notify(`批量出售 ${count} 件装备，获得 ${gold} 金币。`);
    }
    return { count, gold };
  }

  useItem(itemId: number): boolean {
    if (this.state.battle) return this.useBattleItem(itemId);
    const item = this.content.indexes.items.get(itemId);
    if (!item || !this.canUseItem(itemId)) return false;
    const restored = this.applyRecoveryItem(item);
    const learned: string[] = [];
    const forgotten = new Set(
      item.effects.filter((effect) => effect.code === 44).map((effect) => effect.params[0]),
    );
    for (const skillId of forgotten) {
      this.state.player.learnedSkills = this.state.player.learnedSkills.filter(
        (entry) => entry !== skillId,
      );
    }
    for (const effect of item.effects) {
      if (effect.code !== 43) continue;
      const skillId = effect.params[0];
      const skill = skillId === undefined ? undefined : this.content.indexes.skills.get(skillId);
      if (!skill || this.state.player.learnedSkills.includes(skill.id)) continue;
      this.state.player.learnedSkills.push(skill.id);
      learned.push(skill.name);
    }
    const destination = this.itemTransferDestination(item);
    if (destination) {
      this.state.player.currentMapId = destination.map.id;
      this.state.player.x = destination.position.x;
      this.state.player.y = destination.position.y;
      this.state.player.facingX = 0;
      this.state.player.facingY = 1;
      this.state.openPanel = null;
      this.ensureMapRuntime(destination.map);
    }
    const used = restored.hp > 0 || restored.mp > 0 || learned.length > 0 || Boolean(destination);
    if (used && item.consumable) this.state.player.items[itemId] -= 1;
    if (destination) this.notify(`使用${item.name}，返回${destination.map.name}。`);
    else if (learned.length > 0) this.notify(`习得技能：${learned.join("、")}`);
    else if (used) this.notify(`使用了 ${item.name}`);
    return used;
  }

  canUseItem(itemId: number): boolean {
    if (this.state.battle) return false;
    const item = this.content.indexes.items.get(itemId);
    if (
      !item
      || (this.state.player.items[itemId] ?? 0) <= 0
      || (item.usePlace !== 1 && item.usePlace !== 3)
      || this.state.player.level < item.requiredLevel
    ) {
      return false;
    }
    const stats = this.playerStats();
    return item.effects.some((effect) =>
      (effect.code === 11 && this.state.player.hp < stats.maxHp)
      || (effect.code === 12 && this.state.player.mp < stats.maxMp)
      || (
        effect.code === 43
        && effect.params[0] !== undefined
        && this.content.indexes.skills.has(effect.params[0])
        && !this.state.player.learnedSkills.includes(effect.params[0])
      )
      || (effect.code === 51 && this.itemTransferDestination(item) !== null)
    );
  }

  private itemTransferDestination(item: V1Item): {
    map: V1Map;
    position: { x: number; y: number };
  } | null {
    const effect = item.effects.find((entry) => entry.code === 51);
    const mapId = effect?.params[0];
    if (mapId === undefined) return null;
    const map = this.content.indexes.maps.get(mapId);
    if (!map) return null;
    const entryId = effect?.params[1] || map.defaultEntryId;
    try {
      return { map, position: this.entryPosition(map.id, entryId) };
    } catch {
      return null;
    }
  }

  availableBattleItems(): V1Item[] {
    const battle = this.state.battle;
    if (!battle || battle.status !== "active") return [];
    return this.content.items.filter((item) =>
      (this.state.player.items[item.id] ?? 0) > 0
      && (item.usePlace === 2 || item.usePlace === 3)
      && item.effects.some((effect) => effect.code === 11 || effect.code === 12)
    );
  }

  canUseBattleItem(itemId: number): boolean {
    const battle = this.state.battle;
    const item = this.availableBattleItems().find((entry) => entry.id === itemId);
    if (!battle || battle.pendingEnemyTurn || !item) return false;
    const stats = this.playerStats();
    return item.effects.some(
      (effect) =>
        (effect.code === 11 && this.state.player.hp < stats.maxHp)
        || (effect.code === 12 && this.state.player.mp < stats.maxMp),
    );
  }

  useBattleItem(itemId: number): boolean {
    const battle = this.state.battle;
    const item = this.availableBattleItems().find((entry) => entry.id === itemId);
    if (!battle || battle.pendingEnemyTurn || !item || !this.canUseBattleItem(itemId)) {
      return false;
    }
    const restored = this.applyRecoveryItem(item);
    const amount = restored.hp + restored.mp;
    if (amount <= 0) return false;
    if (item.consumable) this.state.player.items[itemId] -= 1;
    this.state.combatSequence += 1;
    this.state.combatFx = {
      sequence: this.state.combatSequence,
      animationId: item.animationId || 1,
      targetIndex: -1,
      amount,
      durationMs: 850,
      kind: "healing",
    };
    const recovery = [
      restored.hp > 0 ? `HP +${restored.hp}` : "",
      restored.mp > 0 ? `MP +${restored.mp}` : "",
    ].filter(Boolean).join("、");
    battle.log.unshift(`${this.content.actor.name}使用${item.name}，${recovery}。`);
    battle.pendingEnemyTurn = true;
    battle.pendingEnemyMs = 900;
    this.emit();
    return true;
  }

  private applyRecoveryItem(item: V1Item): { hp: number; mp: number } {
    const beforeHp = this.state.player.hp;
    const beforeMp = this.state.player.mp;
    const stats = this.playerStats();
    for (const effect of item.effects) {
      if (effect.code === 11 && this.state.player.hp < stats.maxHp) {
        const amount = Math.floor(stats.maxHp * (effect.params[0] ?? 0) / 100) + (effect.params[1] ?? 0);
        this.state.player.hp = Math.min(stats.maxHp, this.state.player.hp + amount);
      } else if (effect.code === 12 && this.state.player.mp < stats.maxMp) {
        const amount = Math.floor(stats.maxMp * (effect.params[0] ?? 0) / 100) + (effect.params[1] ?? 0);
        this.state.player.mp = Math.min(stats.maxMp, this.state.player.mp + amount);
      }
    }
    return {
      hp: this.state.player.hp - beforeHp,
      mp: this.state.player.mp - beforeMp,
    };
  }

  buyItem(shopId: number, itemId: number): boolean {
    const shop = this.content.indexes.shops.get(shopId);
    const item = this.content.indexes.items.get(itemId);
    const good = shop?.goods.find((entry) => entry.entryType === 1 && entry.entryId === itemId);
    if (!shop?.buyEnabled || !item || !good) return false;
    const price = Math.ceil((good.priceOverride ?? item.buyPrice) * shop.buyRatePercent / 100);
    if (this.state.player.gold < price) {
      this.notify("金币不足。");
      return false;
    }
    this.state.player.gold -= price;
    this.state.player.items[itemId] = (this.state.player.items[itemId] ?? 0) + 1;
    this.notify(`购买 ${item.name}，花费 ${price} 金币。`);
    return true;
  }

  playerStats(): V1Stats {
    const level = this.content.indexes.levels.get(this.state.player.level);
    if (!level) throw new Error(`等级数据不存在：${this.state.player.level}`);
    const result: V1Stats = {
      maxHp: level.maxHp,
      maxMp: level.maxMp,
      attack: level.attack,
      defense: level.defense,
      magicAttack: level.magicAttack,
      magicDefense: level.magicDefense,
      agility: level.agility,
      luck: level.luck,
    };
    for (const skillId of this.effectivePlayerSkillIds()) {
      const skill = this.content.indexes.skills.get(skillId);
      if (skill?.type === 2) addStats(result, skill);
    }
    for (const instance of Object.values(this.state.player.equipment)) {
      addStats(result, this.deriveEquipment(instance).stats);
    }
    const percentBonuses = Array<number>(STAT_KEYS.length).fill(0);
    for (const effect of this.activeSetEffects()) {
      const [code, statIdValue, amountValue] = effect.split("|");
      const statId = Number(statIdValue);
      const amount = Number(amountValue);
      const stat = STAT_KEYS[statId];
      if (!stat || !Number.isFinite(amount)) continue;
      if (code === "1") result[stat] += amount;
      if (code === "2") percentBonuses[statId] = (percentBonuses[statId] ?? 0) + amount;
    }
    STAT_KEYS.forEach((stat, statId) => {
      result[stat] = Math.round(result[stat] * (1 + (percentBonuses[statId] ?? 0) / 100));
    });
    return v1ApplyParameterRates(result, this.playerTraits());
  }

  equipmentInventory(): V1DerivedEquipment[] {
    return this.state.player.inventory.map((entry) => this.deriveEquipment(entry));
  }

  equippedItems(): V1DerivedEquipment[] {
    return Object.values(this.state.player.equipment).map((entry) => this.deriveEquipment(entry));
  }

  monstersOnCurrentMap(): V1NormalMonster[] {
    return [...this.currentMapRuntime().normalMonsters];
  }

  livingBossesOnCurrentMap(): Array<{ x: number; y: number; boss: V1MapBoss; key: string }> {
    const map = this.currentMap();
    const runtime = this.currentMapRuntime();
    const result: Array<{ x: number; y: number; boss: V1MapBoss; key: string }> = [];
    for (let y = 0; y < map.height; y += 1) {
      for (let x = 0; x < map.width; x += 1) {
        const special = map.cells[y]?.[x]?.special;
        if (special?.type !== 2) continue;
        const key = cellKey(map.id, x, y, special.troopId);
        if (!runtime.bosses[key]) result.push({ x, y, boss: special, key });
      }
    }
    return result;
  }

  pathTo(targetX: number, targetY: number, allowOccupiedTarget = true): Array<{ dx: number; dy: number }> {
    const map = this.currentMap();
    const start = { x: this.state.player.x, y: this.state.player.y };
    const queue = [start];
    const parent = new Map<string, { previous: string; dx: number; dy: number }>();
    const visited = new Set([`${start.x}:${start.y}`]);
    const monsters = new Set(this.monstersOnCurrentMap().map((entry) => `${entry.x}:${entry.y}`));
    for (let cursor = 0; cursor < queue.length; cursor += 1) {
      const current = queue[cursor];
      if (!current) continue;
      if (current.x === targetX && current.y === targetY) break;
      for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
        const x = current.x + dx;
        const y = current.y + dy;
        const key = `${x}:${y}`;
        const cell = map.cells[y]?.[x];
        if (!cell || !this.isWalkable(cell) || visited.has(key)) continue;
        const interactive = cell.special?.type === 3 || cell.special?.type === 4;
        const occupied = interactive
          || monsters.has(key)
          || this.livingBossesOnCurrentMap().some((entry) => entry.x === x && entry.y === y);
        if (occupied && !(allowOccupiedTarget && x === targetX && y === targetY)) continue;
        visited.add(key);
        parent.set(key, { previous: `${current.x}:${current.y}`, dx, dy });
        queue.push({ x, y });
      }
    }
    const targetKey = `${targetX}:${targetY}`;
    if (!visited.has(targetKey)) return [];
    const path: Array<{ dx: number; dy: number }> = [];
    let key = targetKey;
    const startKey = `${start.x}:${start.y}`;
    while (key !== startKey) {
      const step = parent.get(key);
      if (!step) return [];
      path.push({ dx: step.dx, dy: step.dy });
      key = step.previous;
    }
    return path.reverse();
  }

  private entryPosition(mapId: number, entryId: number): { x: number; y: number } {
    const map = this.content.indexes.maps.get(mapId);
    if (!map) throw new Error(`入口地图不存在：${mapId}`);
    for (let y = 0; y < map.height; y += 1) {
      for (let x = 0; x < map.width; x += 1) {
        const special = map.cells[y]?.[x]?.special;
        if (special?.type === 5 && special.entryId === entryId) return { x, y };
      }
    }
    throw new Error(`地图 ${mapId} 入口 ${entryId} 不存在`);
  }

  private bossAtEncounterKey(key: string): V1MapBoss | null {
    const parts = key.split(":");
    const x = Number(parts[2]);
    const y = Number(parts[3]);
    const special = this.currentMap().cells[y]?.[x]?.special;
    return special?.type === 2 ? special : null;
  }

  private revalidateEquipmentInstances(sourceContentHash: string): void {
    const migrateLegacyBossAbility = (instance: V1EquipmentInstance): void => {
      if (sourceContentHash === this.content.manifest.contentHash || instance.bossEnemyId === undefined) return;
      const pool = this.content.indexes.bossLoot.get(instance.bossEnemyId)?.abilityAffixPool;
      if (!pool || pool.length === 0) return;
      for (const roll of instance.affixRolls) {
        if (roll.lineNo !== 99 || roll.affixId < 58 || roll.affixId > 65) continue;
        roll.affixId = pool[(roll.affixId - 58) % pool.length] ?? pool[0]!;
      }
    };
    for (const instance of this.state.player.inventory) migrateLegacyBossAbility(instance);
    for (const instance of Object.values(this.state.player.equipment)) migrateLegacyBossAbility(instance);
    for (const instance of this.state.player.inventory) this.deriveEquipment(instance);
    const migrated: Record<number, V1EquipmentInstance> = {};
    for (const [rawSlotId, instance] of Object.entries(this.state.player.equipment)) {
      this.deriveEquipment(instance);
      const base = this.content.indexes.equipment.get(instance.equipmentId);
      const candidateSlots = base
        ? v1EquipmentSlotsForType(base.equipmentType)
        : [];
      const savedSlotId = Number(rawSlotId);
      const target = candidateSlots.find(
        (candidate) => candidate.slotId === savedSlotId && !migrated[candidate.slotId],
      ) ?? candidateSlots.find((candidate) => !migrated[candidate.slotId]);
      if (target) migrated[target.slotId] = instance;
      else this.state.player.inventory.push(instance);
    }
    this.state.player.equipment = migrated;
  }
}

type V1MapSpecialLike = NonNullable<V1MapCell["special"]>;
